/**
 * Router-compat shim — bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Outlet as TSOutlet,
  RouterProvider,
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import { parseRouterSearch, stringifyRouterSearch } from "@/lib/routerSearch";

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search?: Record<string, string | string[]>; hash?: string } {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = beforeHash.split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? parseRouterSearch(searchStr) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    tsNav({
      to: pathname,
      search: search as never,
      hash,
      state: options?.state as never,
      replace: options?.replace,
    });
  }, [tsNav, router]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      // REGEL: TanStack liefert searchStr bereits mit führendem "?".
      search: loc.searchStr ? (loc.searchStr.startsWith("?") ? loc.searchStr : `?${loc.searchStr}`) : "",
      // REGEL: React-Router-Verbraucher erwarten die Sprungmarke mit führendem #.
      hash: loc.hash ? (loc.hash.startsWith("#") ? loc.hash : `#${loc.hash}`) : "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

const CompatParamsContext = createContext<Record<string, string> | null>(null);

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  const compat = useContext(CompatParamsContext);
  const params = tsParams({ strict: false } as never) as Record<string, string | undefined>;
  if (compat) return compat as T;
  // react-router nannte den Rest einer *-Adresse "*", TanStack "_splat".
  if (params && "_splat" in params && !("*" in params)) return { ...params, "*": params._splat } as unknown as T;
  return params as T;
}

// ---------- useInRouterContext ----------

export function useInRouterContext(): boolean {
  return !!useRouter({ warn: false } as never);
}


// ---------- useSearchParams (@/lib/router-compat compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj = parseRouterSearch(next.toString());
      // REGEL: Filter-/Reiteränderungen erhalten die vorhandene Sprungmarke.
      nav({ to: live.pathname, search: searchObj as never, hash: live.hash, replace: opts?.replace });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      hash={hash}
      replace={replace}
      state={state as never}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

// REGEL: Eine Weiterleitung wird je Ziel genau einmal ausgelöst. TanStacks <Navigate> navigiert bei
// jedem neuen props-Objekt erneut; mit react-router-Schreibweise (neues state-Objekt je Render)
// entstand so eine Endlosschleife ("Maximum update depth exceeded") im geschützten Bereich.
export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const navigate = useNavigate();
  const doneFor = useRef<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    if (doneFor.current === to) return;
    doneFor.current = to;
    navigate(to, { replace, state: stateRef.current });
  }, [to, replace, navigate]);
  return null;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;


// ---------- Test-Hilfen: MemoryRouter / Routes / Route (react-router-Schreibweise) ----------

/** Rendert Kinder in einem eigenen Speicher-Router (nur für Tests und isolierte Vorschauen). */
export function MemoryRouter({ children, initialEntries = ["/"] }: { children?: ReactNode; initialEntries?: string[] }) {
  const childrenRef = useRef(children);
  childrenRef.current = children;
  const router = useMemo(() => {
    const rootRoute = createRootRoute({ component: () => <>{childrenRef.current}</> });
    const index = createRoute({ getParentRoute: () => rootRoute, path: "/", component: () => null });
    const splat = createRoute({ getParentRoute: () => rootRoute, path: "$", component: () => null });
    return createRouter({
      routeTree: rootRoute.addChildren([index, splat]),
      parseSearch: parseRouterSearch,
      stringifySearch: stringifyRouterSearch,
      history: createMemoryHistory({ initialEntries }),
    });
    // Startadresse gilt nur beim ersten Aufbau, wie bei react-router.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <RouterProvider router={router as never} />;
}

type CompatRouteProps = { path?: string; element?: ReactNode; index?: boolean };

export function Route(_props: CompatRouteProps): null {
  return null;
}

function matchCompatPath(pattern: string, pathname: string): Record<string, string> | null {
  if (pattern === "*") return { "*": pathname.replace(/^\//, "") };
  const p = pattern.replace(/\/+$/, "").split("/");
  const a = (pathname.replace(/\/+$/, "") || "/").split("/");
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    if (seg === "*") { params["*"] = a.slice(i).join("/"); return params; }
    const val = a[i];
    if (val === undefined) return null;
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(val);
    else if (seg.toLowerCase() !== val.toLowerCase()) return null;
  }
  return a.length === p.length ? params : null;
}

/** Wählt das erste passende <Route path element> (wie react-router), feste Pfade vor "*". */
export function Routes({ children }: { children?: ReactNode }) {
  const { pathname } = useLocation();
  const routes = Children.toArray(children).filter(isValidElement) as ReactElement<CompatRouteProps>[];
  const ordered = [...routes.filter((r) => r.props.path !== "*"), ...routes.filter((r) => r.props.path === "*")];
  for (const r of ordered) {
    const params = matchCompatPath(r.props.path ?? "/", pathname);
    if (params) return <CompatParamsContext.Provider value={params}>{r.props.element}</CompatParamsContext.Provider>;
  }
  return null;
}
